/* ============================================================
 * AYU /ayu-erleben – "Dein Tag mit AYU"
 * GENOpuzzle Erleben adapted for AYU
 * Sections: Hero, Tages-Timeline, Gesundheitstagebuch, Wearables, CTA
 * Each section has its own Phone-Mockup with interactive content
 * Mobile: Auto-scroll to mockup on feature/time selection
 * ============================================================ */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "wouter";
import { IMAGES } from "@/lib/constants";
import { useI18n } from "@/contexts/I18nContext";
import {
  Sun,
  Utensils,
  Moon,
  Coffee,
  Leaf,
  Heart,
  BarChart3,
  Dna,
  Lock,
  ChevronRight,
  ArrowRight,
  Watch,
  Activity,
  Thermometer,
  Footprints,
  Scale,
  Timer,
  TestTube,
  PenLine,
  MessageSquare,
  CalendarDays,
  TrendingUp,
} from "lucide-react";

/* ══════════════════════════════════════════════════════════════
   SHARED PHONE MOCKUP (reusable across sections)
   ══════════════════════════════════════════════════════════════ */

type ChatMsg = {
  type: "user" | "bot" | "date";
  text?: string;
  label?: string;
  highlight?: string;
};

function TypingDots() {
  return (
    <div className="flex justify-start">
      <div
        className="px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5"
        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.04)" }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce"
            style={{ animationDelay: `${i * 150}ms`, animationDuration: "0.8s" }}
          />
        ))}
      </div>
    </div>
  );
}

function Bubble({ msg, visible }: { msg: ChatMsg; visible: boolean }) {
  if (msg.type === "date") {
    return (
      <div
        className="text-center transition-all duration-500"
        style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(8px)" }}
      >
        <span className="text-[10px] text-white/30 px-3 py-1 rounded-full bg-white/5 font-medium">
          {msg.text}
        </span>
      </div>
    );
  }
  if (msg.type === "user") {
    return (
      <div
        className="flex justify-end transition-all duration-500"
        style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.95)" }}
      >
        <div
          className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-[11.5px] leading-[1.5]"
          style={{ background: "oklch(0.52 0.09 128)", color: "white" }}
        >
          {msg.text}
        </div>
      </div>
    );
  }
  return (
    <div
      className="flex justify-start transition-all duration-500"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.95)" }}
    >
      <div
        className="max-w-[88%] px-3.5 py-2.5 rounded-2xl rounded-bl-md text-[11.5px] leading-[1.55]"
        style={{ background: "rgba(255,255,255,0.08)", color: "oklch(0.88 0.01 80)", border: "1px solid rgba(255,255,255,0.06)" }}
      >
        {msg.label && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[oklch(0.52_0.09_128)] text-[9px]">&#10022;</span>
            <span className="text-[oklch(0.78_0.11_85)] text-[9px] font-semibold tracking-wider uppercase">{msg.label}</span>
          </div>
        )}
        {msg.highlight ? (
          <span>{msg.text}<strong style={{ color: "oklch(0.78 0.11 85)" }}>{msg.highlight}</strong></span>
        ) : (
          <span style={{ whiteSpace: "pre-line" }}>{msg.text}</span>
        )}
      </div>
    </div>
  );
}

function PhoneMockup({
  chat,
  animKey,
  headerTitle,
  headerSub,
  inputPlaceholder,
}: {
  chat: ChatMsg[];
  animKey: number;
  headerTitle: string;
  headerSub: string;
  inputPlaceholder: string;
}) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const scrollBottom = useCallback(() => {
    containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight, behavior: "smooth" });
  }, []);

  const clearAll = useCallback(() => {
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current = [];
  }, []);

  useEffect(() => {
    clearAll();
    setVisibleCount(0);
    setShowTyping(false);
    let idx = 0;

    const showNext = () => {
      if (idx >= chat.length) {
        setShowTyping(true);
        timeoutsRef.current.push(setTimeout(scrollBottom, 100));
        return;
      }
      const msg = chat[idx];
      if (msg.type === "bot") {
        setShowTyping(true);
        timeoutsRef.current.push(setTimeout(scrollBottom, 50));
        const dur = Math.min(600 + (msg.text?.length || 0) * 10, 1800);
        timeoutsRef.current.push(
          setTimeout(() => {
            setShowTyping(false);
            idx++;
            setVisibleCount(idx);
            timeoutsRef.current.push(setTimeout(scrollBottom, 50));
            timeoutsRef.current.push(setTimeout(showNext, 350));
          }, dur)
        );
      } else if (msg.type === "date") {
        idx++;
        setVisibleCount(idx);
        timeoutsRef.current.push(setTimeout(scrollBottom, 50));
        timeoutsRef.current.push(setTimeout(showNext, 500));
      } else {
        idx++;
        setVisibleCount(idx);
        timeoutsRef.current.push(setTimeout(scrollBottom, 50));
        timeoutsRef.current.push(setTimeout(showNext, 600));
      }
    };
    timeoutsRef.current.push(setTimeout(showNext, 400));
    return () => clearAll();
  }, [chat, animKey, scrollBottom, clearAll]);

  return (
    <div className="relative mx-auto" style={{ width: "280px" }}>
      <div
        className="relative rounded-[40px] overflow-hidden shadow-2xl"
        style={{ background: "linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 50%, #111 100%)", padding: "12px", border: "1px solid rgba(255,255,255,0.1)" }}
      >
        <div className="relative rounded-[30px] overflow-hidden" style={{ background: "#0f1a0f", height: "520px" }}>
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-3 pb-1 relative">
            <span className="text-[11px] font-semibold text-white/80">9:41</span>
            <div className="w-[90px] h-[28px] bg-black rounded-full" style={{ position: "absolute", top: "10px", left: "50%", transform: "translateX(-50%)" }} />
            <div className="flex items-center gap-1">
              <svg width="14" height="10" viewBox="0 0 14 10" fill="white" opacity="0.8"><rect x="0" y="6" width="2.5" height="4" rx="0.5" /><rect x="3.5" y="4" width="2.5" height="6" rx="0.5" /><rect x="7" y="2" width="2.5" height="8" rx="0.5" /><rect x="10.5" y="0" width="2.5" height="10" rx="0.5" /></svg>
              <svg width="22" height="10" viewBox="0 0 25 11" fill="none" stroke="white" opacity="0.8"><rect x="0.5" y="0.5" width="20" height="10" rx="2" strokeWidth="1" /><rect x="22" y="3" width="2" height="5" rx="1" fill="white" opacity="0.4" /><rect x="1.5" y="1.5" width="14" height="8" rx="1" fill="white" opacity="0.8" /></svg>
            </div>
          </div>

          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0" style={{ background: "linear-gradient(135deg, oklch(0.52 0.09 128), oklch(0.42 0.09 128))" }}>
              <div className="w-full h-full flex items-center justify-center">
                <img src={IMAGES.logo} alt="AYU" className="w-5 h-5 object-contain" />
              </div>
            </div>
            <div>
              <div className="text-white text-[13px] font-semibold leading-tight">{headerTitle}</div>
              <div className="text-[oklch(0.52_0.09_128)] text-[10px] leading-tight">{headerSub}</div>
            </div>
            <div className="ml-auto w-2 h-2 rounded-full bg-[oklch(0.52_0.09_128)] animate-pulse" />
          </div>

          {/* Messages */}
          <div
            ref={containerRef}
            className="px-3 py-4 space-y-3 overflow-y-auto"
            style={{ height: "calc(100% - 130px)", scrollbarWidth: "none" }}
          >
            <style>{`div::-webkit-scrollbar { display: none; }`}</style>
            {chat.map((msg, i) => (
              <Bubble key={`${animKey}-${i}`} msg={msg} visible={i < visibleCount} />
            ))}
            {showTyping && <TypingDots />}
            <div className="h-12" />
          </div>

          {/* Input */}
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-6 pt-3" style={{ background: "linear-gradient(to top, #0f1a0f 70%, transparent)" }}>
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-full" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <span className="text-white/30 text-[12px] flex-1">{inputPlaceholder}</span>
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "oklch(0.52 0.09 128)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" /></svg>
              </div>
            </div>
            <div className="w-[120px] h-[4px] bg-white/20 rounded-full mx-auto mt-3" />
          </div>
        </div>
      </div>
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[80%] h-8 bg-[oklch(0.52_0.09_128/0.15)] blur-xl rounded-full" />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   KEY-BASED DATA DEFINITIONS
   ══════════════════════════════════════════════════════════════ */

type ChatMsgKey = {
  type: "user" | "bot" | "date";
  textKey: string;
  labelKey?: string;
};

const TIMELINE_DEFS = [
  {
    time: "07:00",
    titleKey: "ayuErleben.timeline.slot1Title",
    icon: Sun,
    chat: [
      { type: "date" as const, textKey: "ayuErleben.timeline.slot1DateLabel" },
      { type: "bot" as const, labelKey: "ayuErleben.timeline.labelCoach", textKey: "ayuErleben.timeline.slot1Bot1" },
      { type: "bot" as const, textKey: "ayuErleben.timeline.slot1Bot2" },
      { type: "user" as const, textKey: "ayuErleben.timeline.slot1User1" },
      { type: "bot" as const, labelKey: "ayuErleben.timeline.labelRec", textKey: "ayuErleben.timeline.slot1Bot3" },
    ] as ChatMsgKey[],
  },
  {
    time: "12:30",
    titleKey: "ayuErleben.timeline.slot2Title",
    icon: Utensils,
    chat: [
      { type: "date" as const, textKey: "ayuErleben.timeline.slot2DateLabel" },
      { type: "bot" as const, labelKey: "ayuErleben.timeline.labelCoach", textKey: "ayuErleben.timeline.slot2Bot1" },
      { type: "user" as const, textKey: "ayuErleben.timeline.slot2User1" },
      { type: "bot" as const, textKey: "ayuErleben.timeline.slot2Bot2" },
      { type: "bot" as const, labelKey: "ayuErleben.timeline.labelTip", textKey: "ayuErleben.timeline.slot2Bot3" },
    ] as ChatMsgKey[],
  },
  {
    time: "16:00",
    titleKey: "ayuErleben.timeline.slot3Title",
    icon: Coffee,
    chat: [
      { type: "date" as const, textKey: "ayuErleben.timeline.slot3DateLabel" },
      { type: "bot" as const, labelKey: "ayuErleben.timeline.labelCoach", textKey: "ayuErleben.timeline.slot3Bot1" },
      { type: "user" as const, textKey: "ayuErleben.timeline.slot3User1" },
      { type: "bot" as const, textKey: "ayuErleben.timeline.slot3Bot2" },
      { type: "bot" as const, labelKey: "ayuErleben.timeline.labelBreath", textKey: "ayuErleben.timeline.slot3Bot3" },
    ] as ChatMsgKey[],
  },
  {
    time: "21:00",
    titleKey: "ayuErleben.timeline.slot4Title",
    icon: Moon,
    chat: [
      { type: "date" as const, textKey: "ayuErleben.timeline.slot4DateLabel" },
      { type: "bot" as const, labelKey: "ayuErleben.timeline.labelCoach", textKey: "ayuErleben.timeline.slot4Bot1" },
      { type: "user" as const, textKey: "ayuErleben.timeline.slot4User1" },
      { type: "bot" as const, textKey: "ayuErleben.timeline.slot4Bot2" },
      { type: "bot" as const, labelKey: "ayuErleben.timeline.labelBalance", textKey: "ayuErleben.timeline.slot4Bot3" },
    ] as ChatMsgKey[],
  },
];

const DIARY_DEFS = [
  {
    icon: PenLine,
    titleKey: "ayuErleben.diary.f1Title",
    descKey: "ayuErleben.diary.f1Desc",
    chat: [
      { type: "date" as const, textKey: "ayuErleben.diary.f1DateLabel" },
      { type: "user" as const, textKey: "ayuErleben.diary.f1User1" },
      { type: "bot" as const, labelKey: "ayuErleben.diary.labelReflect", textKey: "ayuErleben.diary.f1Bot1" },
      { type: "bot" as const, textKey: "ayuErleben.diary.f1Bot2" },
    ] as ChatMsgKey[],
  },
  {
    icon: MessageSquare,
    titleKey: "ayuErleben.diary.f2Title",
    descKey: "ayuErleben.diary.f2Desc",
    chat: [
      { type: "date" as const, textKey: "ayuErleben.diary.f2DateLabel" },
      { type: "user" as const, textKey: "ayuErleben.diary.f2User1" },
      { type: "bot" as const, labelKey: "ayuErleben.diary.labelPattern", textKey: "ayuErleben.diary.f2Bot1" },
      { type: "bot" as const, textKey: "ayuErleben.diary.f2Bot2" },
    ] as ChatMsgKey[],
  },
  {
    icon: CalendarDays,
    titleKey: "ayuErleben.diary.f3Title",
    descKey: "ayuErleben.diary.f3Desc",
    chat: [
      { type: "date" as const, textKey: "ayuErleben.diary.f3DateLabel" },
      { type: "bot" as const, labelKey: "ayuErleben.diary.labelWeekly", textKey: "ayuErleben.diary.f3Bot1" },
      { type: "bot" as const, textKey: "ayuErleben.diary.f3Bot2" },
      { type: "user" as const, textKey: "ayuErleben.diary.f3User1" },
    ] as ChatMsgKey[],
  },
  {
    icon: TrendingUp,
    titleKey: "ayuErleben.diary.f4Title",
    descKey: "ayuErleben.diary.f4Desc",
    chat: [
      { type: "date" as const, textKey: "ayuErleben.diary.f4DateLabel" },
      { type: "bot" as const, labelKey: "ayuErleben.diary.labelMonthly", textKey: "ayuErleben.diary.f4Bot1" },
      { type: "bot" as const, textKey: "ayuErleben.diary.f4Bot2" },
      { type: "bot" as const, labelKey: "ayuErleben.diary.labelReflect", textKey: "ayuErleben.diary.f4Bot3" },
    ] as ChatMsgKey[],
  },
];

const WEARABLE_DEFS = [
  { icon: Watch, nameKey: "ayuErleben.wearables.ouraName", metricsKey: "ayuErleben.wearables.ouraMetrics" },
  { icon: Activity, nameKey: "ayuErleben.wearables.whoopName", metricsKey: "ayuErleben.wearables.whoopMetrics" },
  { icon: Footprints, nameKey: "ayuErleben.wearables.garminName", metricsKey: "ayuErleben.wearables.garminMetrics" },
  { icon: Timer, nameKey: "ayuErleben.wearables.fitbitName", metricsKey: "ayuErleben.wearables.fitbitMetrics" },
  { icon: Scale, nameKey: "ayuErleben.wearables.withingsName", metricsKey: "ayuErleben.wearables.withingsMetrics" },
  { icon: Thermometer, nameKey: "ayuErleben.wearables.polarName", metricsKey: "ayuErleben.wearables.polarMetrics" },
  { icon: TestTube, nameKey: "ayuErleben.wearables.bloodName", metricsKey: "ayuErleben.wearables.bloodMetrics" },
];

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════ */

export default function AyuErleben() {
  const { t } = useI18n();

  const [activeTime, setActiveTime] = useState(0);
  const [timeAnimKey, setTimeAnimKey] = useState(0);
  const timePhoneRef = useRef<HTMLDivElement>(null);

  const [activeDiary, setActiveDiary] = useState(0);
  const [diaryAnimKey, setDiaryAnimKey] = useState(0);
  const diaryPhoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const resolveChat = useCallback(
    (defs: ChatMsgKey[]): ChatMsg[] =>
      defs.map((msg) => ({
        type: msg.type,
        text: t(msg.textKey as any),
        label: msg.labelKey ? t(msg.labelKey as any) : undefined,
      })),
    [t]
  );

  const TIMELINE = useMemo(
    () => TIMELINE_DEFS.map((slot) => ({ ...slot, title: t(slot.titleKey as any), chat: resolveChat(slot.chat) })),
    [t, resolveChat]
  );

  const DIARY_FEATURES = useMemo(
    () => DIARY_DEFS.map((f) => ({ ...f, title: t(f.titleKey as any), desc: t(f.descKey as any), chat: resolveChat(f.chat) })),
    [t, resolveChat]
  );

  const handleTimeClick = (i: number) => {
    setActiveTime(i);
    setTimeAnimKey((p) => p + 1);
    if (window.innerWidth < 1024 && timePhoneRef.current) {
      timePhoneRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleDiaryClick = (i: number) => {
    setActiveDiary(i);
    setDiaryAnimKey((p) => p + 1);
    if (window.innerWidth < 1024 && diaryPhoneRef.current) {
      diaryPhoneRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className="min-h-screen bg-midnight text-ivory">
      <div className="fixed inset-0 mandala-overlay pointer-events-none" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-midnight/90 backdrop-blur-md border-b border-gold/10">
        <div className="container flex items-center justify-between h-16">
          <Link href="/">
            <img src={IMAGES.logo} alt="AYU" className="h-10" />
          </Link>
          <Link
            href="/funnel"
            className="btn-olive px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2"
          >
            {t("landing.nav.jetztStarten")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="relative py-16 md:py-24">
        <div className="container">
          <span className="inline-block text-gold text-xs tracking-[0.3em] uppercase font-semibold mb-4">
            {t("ayuErleben.hero.badge")}
          </span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("ayuErleben.hero.heading1")}{" "}
            <span className="text-olive-light italic">AYU.</span>
          </h1>
          <p className="text-ivory/60 text-lg max-w-2xl leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
            {t("ayuErleben.hero.description")}
          </p>
        </div>
      </section>

      {/* ═══ TAGES-TIMELINE ═══ */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="text-center mb-12">
            <span className="text-gold text-xs tracking-[0.3em] uppercase font-semibold">
              {t("ayuErleben.timeline.badge")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3" style={{ fontFamily: "var(--font-display)" }}>
              {t("ayuErleben.timeline.heading1")}{" "}
              <span className="text-olive-light italic">{t("ayuErleben.timeline.heading2")}</span>
            </h2>
            <p className="text-ivory/50 mt-3 max-w-xl mx-auto" style={{ fontFamily: "var(--font-body)" }}>
              {t("ayuErleben.timeline.description")}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-5xl mx-auto">
            {/* Left: Timeline Buttons */}
            <div className="space-y-2">
              {TIMELINE.map((slot, i) => {
                const Icon = slot.icon;
                return (
                  <button
                    key={slot.time}
                    onClick={() => handleTimeClick(i)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 text-left cursor-pointer ${
                      activeTime === i
                        ? "border-gold bg-gold/10 text-ivory"
                        : "border-gold/15 bg-midnight-light/40 text-ivory/50 hover:border-gold/30 hover:bg-midnight-light/60"
                    }`}
                  >
                    <div className={`p-2.5 rounded-lg shrink-0 transition-colors duration-300 ${
                      activeTime === i ? "bg-gold text-midnight" : "bg-gold/10 text-gold"
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-mono text-xs opacity-60">{slot.time}</div>
                      <div className="font-semibold text-sm">{slot.title}</div>
                    </div>
                    {activeTime === i && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full bg-gold animate-pulse shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Right: Phone Mockup */}
            <div ref={timePhoneRef} className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[oklch(0.52_0.09_128/0.06)] rounded-full blur-[80px]" />
                <div className="absolute inset-10 bg-[oklch(0.78_0.11_85/0.04)] rounded-full blur-[60px]" />
                <PhoneMockup
                  chat={TIMELINE[activeTime].chat}
                  animKey={timeAnimKey}
                  headerTitle={t("ayuErleben.timeline.phoneTitle")}
                  headerSub={t("ayuErleben.timeline.phoneSub")}
                  inputPlaceholder={t("ayuErleben.timeline.inputPlaceholder")}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ GESUNDHEITSTAGEBUCH ═══ */}
      <section className="py-20 bg-[oklch(0.15_0.02_280)]">
        <div className="container">
          <div className="text-center mb-4">
            <span className="text-gold text-xs tracking-[0.3em] uppercase font-semibold">
              {t("ayuErleben.diary.badge")}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3" style={{ fontFamily: "var(--font-display)" }}>
            {t("ayuErleben.diary.heading1")}{" "}
            <span className="text-olive-light italic">{t("ayuErleben.diary.heading2")}</span>
          </h2>
          <p className="text-ivory/50 text-center max-w-2xl mx-auto mb-12 leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
            {t("ayuErleben.diary.description")}
          </p>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-5xl mx-auto">
            {/* Left: Phone Mockup */}
            <div ref={diaryPhoneRef} className="flex justify-center order-2 lg:order-1">
              <div className="relative">
                <div className="absolute inset-0 bg-[oklch(0.52_0.09_128/0.06)] rounded-full blur-[80px]" />
                <div className="absolute inset-10 bg-[oklch(0.78_0.11_85/0.04)] rounded-full blur-[60px]" />
                <PhoneMockup
                  chat={DIARY_FEATURES[activeDiary].chat}
                  animKey={diaryAnimKey}
                  headerTitle={t("ayuErleben.diary.phoneTitle")}
                  headerSub={t("ayuErleben.diary.phoneSub")}
                  inputPlaceholder={t("ayuErleben.timeline.inputPlaceholder")}
                />
              </div>
            </div>

            {/* Right: Feature Cards */}
            <div className="space-y-3 order-1 lg:order-2">
              {DIARY_FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <button
                    key={f.titleKey}
                    onClick={() => handleDiaryClick(i)}
                    className={`w-full flex items-start gap-4 p-4 rounded-xl border transition-all duration-300 text-left cursor-pointer ${
                      activeDiary === i
                        ? "border-olive bg-olive/10 text-ivory"
                        : "border-gold/10 bg-midnight-light/30 text-ivory/60 hover:border-olive/30 hover:bg-midnight-light/50"
                    }`}
                  >
                    <div className={`p-2.5 rounded-lg shrink-0 transition-colors duration-300 ${
                      activeDiary === i ? "bg-olive text-ivory" : "bg-olive/15 text-olive-light"
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-sm mb-1" style={{ fontFamily: "var(--font-display)" }}>{f.title}</div>
                      <p className="text-xs leading-relaxed opacity-70" style={{ fontFamily: "var(--font-body)" }}>{f.desc}</p>
                    </div>
                    {activeDiary === i && (
                      <div className="ml-auto mt-2 w-1.5 h-1.5 rounded-full bg-olive animate-pulse shrink-0" />
                    )}
                  </button>
                );
              })}

              <Link
                href="/funnel"
                className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-olive-light hover:text-gold transition-colors"
                style={{ fontFamily: "var(--font-body)" }}
              >
                {t("ayuErleben.diary.ctaButton")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ WEARABLES ═══ */}
      <section className="py-20">
        <div className="container text-center">
          <span className="text-gold text-xs tracking-[0.3em] uppercase font-semibold">
            {t("ayuErleben.wearables.badge")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-4" style={{ fontFamily: "var(--font-display)" }}>
            {t("ayuErleben.wearables.heading1")}{" "}
            <span className="text-olive-light italic">{t("ayuErleben.wearables.heading2")}</span>
          </h2>
          <p className="text-ivory/50 max-w-xl mx-auto mb-12" style={{ fontFamily: "var(--font-body)" }}>
            {t("ayuErleben.wearables.description")}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {WEARABLE_DEFS.map((w) => {
              const Icon = w.icon;
              return (
                <div
                  key={w.nameKey}
                  className="p-5 rounded-xl border border-gold/10 bg-midnight-light/30 hover:border-gold/25 hover:bg-midnight-light/50 transition-all duration-300"
                >
                  <div className="text-gold/60 mb-3 flex justify-center">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="font-semibold text-sm text-ivory mb-1">{t(w.nameKey as any)}</div>
                  <div className="text-xs text-ivory/40" style={{ fontFamily: "var(--font-body)" }}>{t(w.metricsKey as any)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-20 relative">
        <div className="absolute inset-0 mandala-overlay pointer-events-none" />
        <div className="container text-center relative z-10">
          <div className="w-14 h-14 rounded-2xl bg-olive/20 flex items-center justify-center mx-auto mb-6">
            <Leaf className="w-7 h-7 text-olive-light" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "var(--font-display)" }}>
            {t("ayuErleben.cta.heading1")}
            <br />
            <span className="text-olive-light italic">{t("ayuErleben.cta.heading2")}</span>
          </h2>
          <p className="text-ivory/60 max-w-lg mx-auto mb-8 leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
            {t("ayuErleben.cta.description")}
          </p>
          <Link
            href="/funnel"
            className="inline-flex items-center gap-2 btn-olive px-8 py-4 rounded-xl text-base font-semibold"
          >
            {t("ayuErleben.cta.button")}
            <ChevronRight className="w-5 h-5" />
          </Link>
          <div className="flex items-center justify-center gap-6 mt-5 text-xs text-ivory/40">
            <span className="flex items-center gap-1.5">
              <div className="w-1 h-1 rounded-full bg-olive" />
              {t("ayuErleben.cta.time")}
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1 h-1 rounded-full bg-olive" />
              {t("ayuErleben.cta.free")}
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1 h-1 rounded-full bg-olive" />
              {t("ayuErleben.cta.noObligation")}
            </span>
          </div>

          <div className="mt-6">
            <Link href="/" className="text-sm text-ivory/40 hover:text-gold transition-colors">
              {t("ayuErleben.cta.backLink")}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-midnight border-t border-gold/10">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="AYU" className="h-8 opacity-60" />
            <span className="text-ivory/30 text-sm">
              &copy; {new Date().getFullYear()} {t("landing.footer.copyright")}
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-ivory/40">
            <Link href="/impressum" className="hover:text-gold transition-colors">{t("landing.footer.impressum")}</Link>
            <Link href="/datenschutz" className="hover:text-gold transition-colors">{t("landing.footer.datenschutz")}</Link>
            <Link href="/agb" className="hover:text-gold transition-colors">{t("landing.footer.agb")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
